import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Tag, Clock, XCircle } from 'lucide-react';

const BlogArticleGrossistesVinted: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Grossistes Vinted : ce que personne ne te dit vraiment | SmartResell</title>
        <meta name="description" content="Délais de 6 mois, tailles invendables, qualité douteuse : la vérité sur les grossistes Vinted avant de commander pour des milliers d'euros de stock." />
        <link rel="canonical" href="https://www.smart-resell.com/blog/grossistes-fournisseurs-vinted" />
        <meta property="og:title" content="Grossistes Vinted : ce que personne ne te dit vraiment | SmartResell" />
        <meta property="og:description" content="Délais de 6 mois, tailles invendables, qualité douteuse : la vérité sur les grossistes Vinted avant de commander pour des milliers d'euros de stock." />
        <meta property="og:url" content="https://www.smart-resell.com/blog/grossistes-fournisseurs-vinted" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://www.smart-resell.com/og-image.png" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": "Grossistes Vinted : ce que personne ne te dit vraiment",
          "description": "Délais de 6 mois, tailles invendables, qualité douteuse : la vérité sur les grossistes Vinted avant de commander pour des milliers d'euros de stock.",
          "url": "https://www.smart-resell.com/blog/grossistes-fournisseurs-vinted",
          "datePublished": "2026-04-20",
          "dateModified": "2026-04-20",
          "inLanguage": "fr",
          "author": { "@type": "Organization", "name": "SmartResell", "url": "https://www.smart-resell.com" },
          "publisher": { "@type": "Organization", "name": "SmartResell", "url": "https://www.smart-resell.com" },
          "mainEntityOfPage": { "@type": "WebPage", "@id": "https://www.smart-resell.com/blog/grossistes-fournisseurs-vinted" }
        })}</script>
      </Helmet>

      {/* Header */}
      <header className="border-b border-gray-100 px-6 py-4 sticky top-0 bg-white/95 backdrop-blur z-10">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => navigate('/')}
          >
            <img src="/logo192.png" alt="SmartResell" className="w-8 h-8 rounded-xl" />
            <span className="font-bold text-lg" style={{ color: '#0d9488' }}>SmartResell</span>
          </div>
          <button
            onClick={() => navigate('/blog')}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft size={15} />
            Blog
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-14">

        {/* Meta */}
        <div className="flex items-center gap-3 mb-5">
          <span className="text-xs font-semibold text-teal-600 bg-teal-50 px-2.5 py-1 rounded-full flex items-center gap-1">
            <Tag size={11} />Stratégie Vinted
          </span>
          <span className="text-xs text-gray-400 flex items-center gap-1">
            <Clock size={11} />5 min de lecture
          </span>
          <span className="text-xs text-gray-400">20 avril 2026</span>
        </div>

        {/* Title */}
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight mb-5">
          📦 Grossistes & fournisseurs Vinted : ce que personne ne te dit vraiment
        </h1>

        {/* Lead */}
        <p className="text-lg text-gray-500 leading-relaxed mb-10 border-l-4 border-teal-400 pl-5">
          Les réseaux sont pleins de "fournisseurs exclusifs" qui promettent des lots à prix cassés. Avant de commander, voilà ce qui t'attend vraiment.
        </p>

        {/* Section 1 — La promesse */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            La promesse : des promos sur la grosse quantité
          </h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            Le pitch est toujours le même : commande en gros, et tu obtiens des prix imbattables. Sur le papier, c'est tentant : tu imagines un stock fourni à moindre coût, revendu avec une belle marge sur Vinted.
          </p>
          <p className="text-gray-600 leading-relaxed">
            La réalité est bien différente.
          </p>
        </section>

        {/* Section 2 — La réalité */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-5">
            Ce que tu reçois vraiment
          </h2>

          <div className="space-y-4">
            {[
              {
                title: 'Des délais de 3 à 6 mois',
                desc: 'On te vend du rêve en quelques clics, mais la livraison prend des mois. Pendant ce temps, ton argent est immobilisé et tu ne peux pas avancer.',
              },
              {
                title: 'Des tailles non recherchées',
                desc: 'Les lots sont remplis de tailles extrêmes : du 3XL, du 4XL, du XS, celles que personne ne cherche sur Vinted. Les tailles courantes, elles, ont déjà été triées avant que le lot t\'arrive.',
              },
              {
                title: 'Des couleurs flashy invendables',
                desc: 'Les coloris sobres et neutres partent en premier. Ce qui reste dans les lots "en gros", ce sont les couleurs criardes que les acheteurs Vinted boudent.',
              },
              {
                title: 'Une qualité déplorable',
                desc: 'Sur les vêtements de marque, il n\'est pas rare de trouver des trous dans le textile, des coutures qui lâchent, des articles clairement inutilisables. La qualité n\'est pas garantie, et l\'authenticité encore moins.',
              },
              {
                title: 'Aucune fiabilité sur l\'authenticité',
                desc: 'Les articles de marque sont parmi les plus recherchés sur Vinted, et parmi les plus copiés. Avec ces lots, tu n\'as aucune certitude que ce que tu revends est authentique. Revendre un faux, même sans le savoir, c\'est ton compte Vinted qui trinque.',
              },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4 bg-gray-50 border border-gray-100 rounded-2xl p-5">
                <XCircle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-gray-900 mb-1">{item.title}</p>
                  <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Section 3 — La question logique */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            La question à se poser
          </h2>
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
            <p className="text-amber-900 font-semibold text-lg leading-snug mb-3">
              Si ce fournisseur avait vraiment accès à des vêtements de qualité à des prix aussi bas… pourquoi les revendrait-il à toi au lieu de les revendre lui-même ?
            </p>
            <p className="text-amber-800 text-sm leading-relaxed">
              C'est la question centrale. Un accès à du stock rentable à bas prix, ça se garde. Ça ne se vend pas sur Instagram pour quelques dizaines d'euros.
            </p>
          </div>
        </section>

        {/* Section 4 — Red flags */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Les signaux d'alarme à repérer immédiatement
          </h2>
          <p className="text-gray-600 leading-relaxed mb-5">
            Certains indices doivent te faire fuir sans hésiter :
          </p>
          <ul className="space-y-2">
            {[
              'Il fait la promotion de ses "contacts exclusifs" sur TikTok, Instagram ou Facebook',
              'Il propose un code promo pour accéder à sa liste de fournisseurs',
              'Il vend ses coordonnées dans un PDF à 19,99 €',
              'Il n\'a ni adresse physique, ni SIRET, ni aucune preuve de son activité',
              'Les témoignages clients sont flous ou impossibles à vérifier',
            ].map((flag, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-gray-600">
                <span className="text-red-400 font-bold mt-0.5 flex-shrink-0">✕</span>
                {flag}
              </li>
            ))}
          </ul>
        </section>

        {/* Section 5 — La meilleure alternative */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            La meilleure alternative : l'achat-revente directement sur Vinted
          </h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            Plutôt que de miser sur des lots douteux, l'une des stratégies les plus fiables reste l'achat-revente directement depuis la plateforme Vinted : tu achètes des articles sous-évalués, tu les remets en valeur, et tu les revends à leur juste prix.
          </p>
          <p className="text-gray-600 leading-relaxed mb-4">
            L'avantage ? Tu contrôles chaque article que tu achètes. Tu vois les photos, les descriptions, l'état réel. Pas de surprise à la livraison, pas de risque sur l'authenticité.
          </p>
          <p className="text-gray-600 leading-relaxed">
            La limite, c'est que gérer ce type d'activité manuellement (achats, ventes, bordereaux, marges, stock) devient vite chronophage dès que le volume augmente.
          </p>
        </section>

        {/* CTA SmartResell */}
        <div className="bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl p-8 text-white mt-12">
          <p className="text-xs font-bold uppercase tracking-widest text-teal-200 mb-2">SmartResell</p>
          <h3 className="text-xl font-bold mb-3 leading-snug">
            On a construit l'outil pour gérer ça automatiquement
          </h3>
          <p className="text-sm text-teal-100 leading-relaxed mb-5">
            SmartResell est une plateforme pensée pour les revendeurs Vinted qui font de l'achat-revente. Gestion des achats, ventes, stock, bordereaux d'expédition, comptabilité et scraping des prix, tout au même endroit.
          </p>
          <a
            href="https://www.smart-resell.com"
            className="inline-flex items-center gap-2 bg-white text-teal-700 font-semibold text-sm px-5 py-2.5 rounded-xl hover:bg-teal-50 transition-colors no-underline"
          >
            Découvrir SmartResell →
          </a>
        </div>

        {/* Maillage interne */}
        <div className="mt-12 pt-8 border-t border-gray-100">
          <p className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4">Voir aussi</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { emoji: '🚀', title: 'Boost Vinted : utile ou mythe ?', slug: 'boost-vinted-utile-ou-mythe', date: '13 avril 2026' },
              { emoji: '🤖', title: 'Bot Vinted : autobuy vs autocop', slug: 'bots-vinted-autobuy-autocop', date: '6 avril 2026' },
            ].map((a) => (
              <button key={a.slug} onClick={() => navigate(`/blog/${a.slug}`)}
                className="text-left bg-gray-50 border border-gray-100 rounded-xl p-4 hover:border-teal-200 hover:bg-teal-50/30 transition-all">
                <p className="text-base font-semibold text-gray-900 mb-1">{a.emoji} {a.title}</p>
                <p className="text-xs text-gray-400">{a.date}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Back link */}
        <div className="mt-6">
          <button
            onClick={() => navigate('/blog')}
            className="flex items-center gap-2 text-sm text-gray-400 hover:text-teal-600 transition-colors"
          >
            <ArrowLeft size={15} />
            Retour au blog
          </button>
        </div>

      </main>

      <footer className="border-t border-gray-100 mt-8 py-8 text-center text-sm text-gray-400">
        <span
          className="font-semibold cursor-pointer hover:text-teal-600 transition-colors"
          onClick={() => navigate('/')}
          style={{ color: '#0d9488' }}
        >
          SmartResell
        </span>
        , l'outil des resellers Vinted pro
      </footer>
    </div>
  );
};

export default BlogArticleGrossistesVinted;
